import { ContentItem, TextContentItem, VideoContentItem, DividerContentItem, ImageContentItem, DocumentContentItem } from '.';

export class Page {
  name: string;
  description: string;
  id: string;
  position: number;
  createdAt: string;
  updatedAt: string;
  firebaseKey: string = '';
  firebaseUrl: string = '';
  pages: Page[] = [];
  parent: Page = null;
  private _type: string;

  // For URL page
  url: string;

  // For custom page
  contentItems: ContentItem[] = [];

  // For training only
  trainingType: string;
  trainingID: number;

  // For folder
  isCollapsed: boolean = true;

  testID: string = '';

  constructor(json: any, parent?: Page, expandedIds?: string[]) {
    this.name = json.name || '';
    this.type = json.content_type || '';
    this.description = json.description || '';
    this.id = json.id;
    this.position = json.position || 0;
    this.firebaseKey = json.$key || '';
    this.createdAt = json.created_at || '';
    this.updatedAt = json.updated_at || '';
    this.firebaseUrl = json.firebase_url || '';

    if (parent) {
      this.parent = parent;
      this.firebaseUrl = parent.firebaseUrl + '/pages/' + json.$key;
    } else if (!json.firebaseUrl) {
      this.firebaseUrl = '/' + json.$key;
    }

    switch (this.type) {
      case 'external':
        this.url = json.url;
        break;
      case 'custom':
        if (typeof json.content === 'string') {
          this.convertPreviousContentToContentItems(json.content);
        } else {
          this.updatePageContentWithJson(json.content);
        }
        break;
      case 'folder':
        if (json.pages) {
          for (let key in json.pages) {
            let obj = json.pages[key];
            obj.$key = key;
            let page = new Page(obj, this, expandedIds);
            if (expandedIds && expandedIds.indexOf(page.id) >= 0) page.isCollapsed = false;
            this.pages.push(page);
          }
        }
        break;
      case 'training':
        this.trainingType = json.training_obj.subtype;
        this.trainingID = json.training_obj.id;
        break;
      default:
        break;
    }

    this.generateDynamicId();
  }

  get type() { return this._type; }

  set type(type: string) {
    this._type = type;
    if (type === 'static' && this.name === 'Events') {
      this.name = 'Calendar';
      this._type = 'calendar';
    }
    if (type != 'custom') {
      this.contentItems = [];
    }
    if (type != 'training') {
      this.trainingID = null;
      this.trainingType = null;
    }
    if (type != 'external') {
      this.url = null;
    }
  }

  expandParents() {
    if (this.parent) {
      this.parent.isCollapsed = false;
      this.parent.expandParents();
    }
  }

  generateDynamicId() {
    let validId = this.name;
    validId = validId.replace(/\s+/g, '-');
    validId = validId.replace(/[^a-z0-9\-_\.]|^[^a-z0-9]+/gi, '');
    this.testID = 'cont-' + validId;
  }

  toFirebaseObject() : any {
    let obj: any = {};
    if (this.name) obj.name = this.name;
    obj.description = this.description;
    if (this.id) obj.id = this.id;
    if (this.position) obj.position = this.position;
    if (this.type) obj.content_type = this.type;
    if (this.createdAt) obj.created_at = this.createdAt;
    if (this.updatedAt) obj.updated_at = this.updatedAt;
    switch (this.type) {
      case 'external':
        if (this.url) obj.url = this.url;
        break;
      case 'custom':
        obj.content = this.contentItems.map(contentItem => contentItem.toJson());
        break;
      case 'folder':
        obj.pages = [];
        this.pages.forEach(page => {
          obj.pages.push(page.toFirebaseObject());
        });
        break;
      case 'training':
        obj.training_obj = {
          subtype: this.trainingType,
          id: this.trainingID
        }
        break;
      default:
        break;
    }
    return obj;
  }

  updatePageContentWithJson(contentItemsJson: any[]) {
    if (contentItemsJson) {
      this.contentItems = contentItemsJson.map(itemJson => {
        switch(itemJson.content_type) {
          case 'text':
            return new TextContentItem(itemJson);
          case 'youtube-video':
          case 'video':
            return new VideoContentItem(itemJson);
          case 'divider':
            return new DividerContentItem();
          case 'image':
            return new ImageContentItem(itemJson);
          case 'pdf':
          case 'spreadsheet':
            return new DocumentContentItem(itemJson);
          default:
            return;
        }
      });
    }
  }

  getChildByUrl(firebaseUrl: string) : Page {
    if (firebaseUrl && firebaseUrl.length) {
      if (this.firebaseUrl === firebaseUrl) {
        return this;
      } else if (firebaseUrl.startsWith(this.firebaseUrl)) {
        for (var i = 0; i < this.pages.length; i++) {
          let page = this.pages[i];
          let result = page.getChildByUrl(firebaseUrl);
          if (result) {
            return result;
          }
        }
      }
    }
    return null;
  }

  getFlatPages() : Page[]{
    let flatPages: Page[] = [];
    let counter: number = 0;

    this.pages.forEach((page) => {
      flatPages.push(page);
      flatPages = flatPages.concat(page.getFlatPages());
    })
    return flatPages;
  }

  removeChild(child: Page) {
    let index = this.pages.indexOf(child);
    if (index > -1) {
      this.pages.splice(index, 1);
    }
    return this.pages;
  }

  insertChild(child: Page, index: number) {
    // Sort the pages and insert the child
    this.pages = this.pages.sort((a, b) => {
      return a.position - b.position;
    });

    this.pages.splice(index, 0, child);

    // Recalculate the positions
    for (var i = 0; i < this.pages.length; i++) {
      this.pages[i].position = i;
    }

    return this.pages;
  }

  convertPreviousContentToContentItems(content: string) {
    let contentItems: ContentItem[] = [];
    let element = document.createElement('div');
    element.innerHTML = content;

    for (var i = 0; i < element.children.length; i++) {
      let child: HTMLElement = <HTMLElement>element.children[i];
      if (child.tagName === 'IMG') {
        contentItems.push(new ImageContentItem({
          url: (<HTMLImageElement>child).src
        }));
      } else if (child.tagName === 'DIV' && child.classList.contains('video-container')) {
        let thumbnail = child.style.backgroundImage.substr(5, child.style.backgroundImage.length - 7);
        let video: HTMLVideoElement = child.getElementsByTagName('video')[0];
        contentItems.push(new VideoContentItem({
          video: video.src,
          thumbnail: thumbnail
        }));
      } else if (child.tagName === 'DIV' && child.classList.contains('image-container')) {
        let image: HTMLImageElement = child.getElementsByTagName('img')[0];
        contentItems.push(new ImageContentItem({
          url: image.src
        }));
      } else if (child.tagName === 'DIV' && child.classList.contains('youtube-container')) {
        let iframe: HTMLIFrameElement = child.getElementsByTagName('iframe')[0];
        contentItems.push(new VideoContentItem({
          embed_link: iframe.outerHTML
        }));
      } else {
        // If the previous child was not a text element
        if (!contentItems.length || contentItems[contentItems.length - 1].type !== 'text' || child.tagName === 'H1') {
          if (child.tagName === 'H1') {
            contentItems.push(new TextContentItem({
              title: child.innerText
            }))
          } else {
            contentItems.push(new TextContentItem({
              text: child.outerHTML
            }))
          }
        } else {
          let previousChild: TextContentItem = <TextContentItem>contentItems[contentItems.length - 1];
          previousChild.text += child.outerHTML;
        }
      }
    }
    this.contentItems = contentItems;
  }
}



// WEBPACK FOOTER //
// /app/src/app/shared/services/app/content/models/page.model.ts